import {useEffect, useState} from 'react';
import {useReactiveVar} from '@apollo/client';
import {Transition} from '@headlessui/react';

import {surveyVar, surveyWizardVars} from '#apollo/local/SurveyVar';
import {TallyLogo} from '#component/base';
import {Tabs} from '#component/partial/Tabs';
import {Question} from '#component/partial/Wizard/component';
import {
  EventType,
  PageViewEvent,
  useAnalytics,
  UserActionEvent,
} from '#component/provider/AnalyticsProvider';
import {SESSION_STORAGE_KEY} from '#constant/survey';
import {sprinkles} from '#style/sprinkles';
import {fadeProps} from '#util/style/transition';

import {
  getInitialStateValues,
  isFirstQuestionInCategory,
  isFirstQuestionInSurvey,
  isLastQuestionInCategory,
  isLastQuestionInSurvey,
} from './Wizard.util';

import {WizardProps} from './Wizard.types';

import * as css from './Wizard.css';

export const Wizard = ({survey, onSubmit}: WizardProps) => {
  const {categories, id: surveyId} = survey;
  const {track} = useAnalytics();
  const savedSurveys = useReactiveVar(surveyVar);
  const surveyAnswers = savedSurveys?.[surveyId] || {};
  const {initialQuestionIndex, initialCategoryIndex} = getInitialStateValues({
    survey,
    surveyAnswers,
  });
  const [currCategoryIndex, setCurrCategoryIndex] = useState(initialCategoryIndex);
  const [currQuestionIndex, setCurrQuestionIndex] = useState(initialQuestionIndex);
  const [transitioning, setTransitioning] = useState(false);
  const categoryNames = categories.map(category => category.key);
  const currentCategory = categories[currCategoryIndex];
  const currentQuestion = currentCategory.steps[currQuestionIndex].question;

  useEffect(
    () => {
      track<PageViewEvent>({
        evtName: EventType.PageView,
        pageName: 'Survey Question',
        data: {
          categoryId: currentCategory.id,
          categoryKey: currentCategory.key,
          questionId: currentQuestion.id,
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentQuestion],
  );

  const handleTabPress = (tabIndex: number) => {
    // Check if previous categories have been completed before allowing a user to press a category tab
    if (tabIndex > currCategoryIndex) {
      const previousCategories = categories.slice(0, tabIndex);

      const previousQuestionIds = previousCategories.reduce<string[]>((acc, category) => {
        const categoryQuestions = category.steps.map(step => step.question.id);
        return [...acc, ...categoryQuestions];
      }, []);

      const hasCompletedAllPreviousCategories = previousQuestionIds.every(
        questionId => surveyAnswers[questionId] !== undefined,
      );

      if (!hasCompletedAllPreviousCategories) {
        return;
      }
    }

    setCurrCategoryIndex(tabIndex);
    setCurrQuestionIndex(0);
    setTransitioning(true);
    window?.scroll({top: 0, left: 0, behavior: 'smooth'});
  };

  const handleNext = () => {
    track<UserActionEvent>({actionName: 'Survey Question Next', evtName: EventType.UserAction});

    window?.scroll({top: 0, left: 0, behavior: 'smooth'});

    if (isLastQuestionInCategory(currQuestionIndex, currentCategory)) {
      if (isLastQuestionInSurvey(currQuestionIndex, currCategoryIndex, categories)) {
        onSubmit();
      } else {
        // handle completion of category by going to the first step of the next category
        setCurrCategoryIndex(currCategoryIndex + 1);
        setCurrQuestionIndex(0);
        setTransitioning(true);

        sessionStorage?.setItem(SESSION_STORAGE_KEY, JSON.stringify(savedSurveys));
      }
    } else {
      // handle next question within a category
      setCurrQuestionIndex(currQuestionIndex + 1);
      setTransitioning(true);

      surveyWizardVars.persist();
    }
  };

  const handleBack = isFirstQuestionInSurvey(categories[0], currentQuestion)
    ? undefined
    : () => {
        track<UserActionEvent>({
          evtName: EventType.UserAction,
          actionName: 'Survey Question Back',
        });
        if (isFirstQuestionInCategory(currQuestionIndex)) {
          /**
           * if we're on the first question in a category, handle going back one category
           * to the final step in the previous category
           */
          const previouscurrCategoryIndex = currCategoryIndex - 1;
          const previousCategory = categories[previouscurrCategoryIndex];
          const lastStepIndex = previousCategory.steps.length - 1;

          setCurrCategoryIndex(previouscurrCategoryIndex);
          setCurrQuestionIndex(lastStepIndex);
          setTransitioning(true);
        } else {
          // handle going back 1 question within a category
          setCurrQuestionIndex(currQuestionIndex - 1);
          setTransitioning(true);
        }
        window?.scroll({top: 0, left: 0, behavior: 'smooth'});
      };

  return (
    <div className={css.root}>
      <div className={sprinkles({content: 'start', flex: 'flex', items: 'center', w: 'full'})}>
        <TallyLogo className={sprinkles({display: ['none', 'none', 'flex']})} size={74} />
        <Tabs
          className={sprinkles({mX: 'auto'})}
          onClick={handleTabPress}
          selected={currCategoryIndex}
          tabNames={categoryNames}
        />
      </div>

      <Transition
        appear
        className={css.transition}
        show={!transitioning}
        unmount={false}
        afterLeave={() => {
          setTransitioning(false);
        }}
        {...fadeProps()}>
        <Question
          /**
           * the key prop here is necessary to ensure a new Instance of the Question component is mounted
           * which will initialize a new internal state preventing data carryover from the previous Question
           */
          key={currentQuestion.id}
          onBack={handleBack}
          onNext={handleNext}
          question={currentQuestion}
          surveyId={surveyId}
        />
      </Transition>
    </div>
  );
};
